import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { todayLocalStr } from '@/utils/dateHelpers'; // F-FIN P2: hoje em SP, não UTC

export interface SearchableReceivable {
  id: string;
  type: 'receivable' | 'payable';
  transaction_number: string;
  origin_number?: string | null;
  description: string;
  due_date: string;
  payment_date: string | null;
  original_value: number;
  total_value: number;
  paid_value: number | null;
  /** F-FIN P2: coluna gerada total − pago − desconto (régua única de saldo) */
  balance?: number | null;
  status: string;
  customer_name?: string;
  supplier_name?: string;
  category_name?: string;
  financial_account_id?: string;
  financial_account_name?: string;
}

export interface SearchReceivablesOptions {
  startDate?: string;
  endDate?: string;
  /** Campo da janela de datas (default: due_date) */
  dateField?: 'due_date' | 'payment_date';
  /** R3/R4/R5: títulos da conta informada OU sem conta (null) */
  accountOrNull?: string;
  searchTerm?: string;
  financialAccountId?: string;
  type?: 'receivable' | 'payable' | 'all';
  status?: 'pending' | 'paid' | 'overdue' | 'all';
  enabled?: boolean;
}

export const useSearchReceivables = (options: SearchReceivablesOptions = {}) => {
  const { companyId } = useMultiTenant();
  const {
    startDate,
    endDate,
    dateField,
    accountOrNull,
    searchTerm,
    financialAccountId,
    type = 'all',
    status = 'all',
    enabled = true
  } = options;

  return useQuery({
    queryKey: ['search-receivables', companyId, startDate, endDate, dateField, accountOrNull, searchTerm, financialAccountId, type, status],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID não disponível');

      const results: SearchableReceivable[] = [];

      // Buscar contas a receber
      if (type === 'all' || type === 'receivable') {
        let query = supabase
          .from('accounts_receivable')
          .select(`
            id,
            transaction_number,
            origin_number,
            description,
            due_date,
            payment_date,
            original_value,
            total_value,
            paid_value,
            balance,
            status,
            financial_account_id,
            customer:customer_id (name),
            income_category:income_category_id (category_name),
            financial_account:financial_account_id (account_name)
          `)
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .order('due_date', { ascending: false });

        // Filtros de status
        if (status === 'pending') {
          query = query.in('status', ['pending', 'partial']);
        } else if (status === 'paid') {
          query = query.eq('status', 'paid');
        } else if (status === 'overdue') {
          query = query.in('status', ['pending', 'partial']).lt('due_date', todayLocalStr()); // F-FIN P2: hoje em SP
        }

        // Filtros de data (campo configurável — D7b)
        const vDateField = dateField ?? 'due_date';
        if (startDate) {
          query = query.gte(vDateField, startDate);
        }
        if (endDate) {
          query = query.lte(vDateField, endDate);
        }

        // Filtro de conta financeira
        if (financialAccountId) {
          query = query.eq('financial_account_id', financialAccountId);
        }
        if (accountOrNull) {
          query = query.or(`financial_account_id.eq.${accountOrNull},financial_account_id.is.null`);
        }

        // Filtro de texto
        if (searchTerm) {
          const ors = [
            `description.ilike.%${searchTerm}%`,
            `transaction_number.ilike.%${searchTerm}%`,
            `origin_number.ilike.%${searchTerm}%`,
          ];
          const digits = searchTerm.replace(/\D/g, '');
          if (digits && digits.length > 0 && digits.length <= 6) {
            ors.push(`origin_number.ilike.%${digits.padStart(6, '0')}%`);
          }
          query = query.or(ors.join(','));
        }

        const { data, error } = await query.limit(100);

        if (error) {
          console.error('Erro ao buscar contas a receber:', error);
        } else if (data) {
          for (const item of data) {
            results.push({
              id: item.id,
              type: 'receivable',
              transaction_number: item.transaction_number,
              origin_number: item.origin_number,
              description: item.description,
              due_date: item.due_date,
              payment_date: item.payment_date,
              original_value: item.original_value,
              total_value: item.total_value,
              paid_value: item.paid_value,
              balance: item.balance,
              status: item.status,
              customer_name: (item.customer as any)?.name,
              category_name: (item.income_category as any)?.category_name,
              financial_account_id: item.financial_account_id || undefined,
              financial_account_name: (item.financial_account as any)?.account_name,
            });
          }
        }
      }

      // Buscar contas a pagar
      if (type === 'all' || type === 'payable') {
        let query = supabase
          .from('accounts_payable')
          .select(`
            id,
            transaction_number,
            origin_number,
            description,
            due_date,
            payment_date,
            original_value,
            total_value,
            paid_value,
            balance,
            status,
            financial_account_id,
            supplier:supplier_id (name),
            expense_category:expense_category_id (category_name),
            financial_account:financial_account_id (account_name)
          `)
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .order('due_date', { ascending: false });

        // Filtros de status
        if (status === 'pending') {
          query = query.in('status', ['pending', 'partial']);
        } else if (status === 'paid') {
          query = query.eq('status', 'paid');
        } else if (status === 'overdue') {
          query = query.in('status', ['pending', 'partial']).lt('due_date', todayLocalStr()); // F-FIN P2: hoje em SP
        }

        // Filtros de data (campo configurável — D7b)
        const vDateField = dateField ?? 'due_date';
        if (startDate) {
          query = query.gte(vDateField, startDate);
        }
        if (endDate) {
          query = query.lte(vDateField, endDate);
        }

        // Filtro de conta financeira
        if (financialAccountId) {
          query = query.eq('financial_account_id', financialAccountId);
        }
        if (accountOrNull) {
          query = query.or(`financial_account_id.eq.${accountOrNull},financial_account_id.is.null`);
        }

        // Filtro de texto
        if (searchTerm) {
          const ors = [
            `description.ilike.%${searchTerm}%`,
            `transaction_number.ilike.%${searchTerm}%`,
            `origin_number.ilike.%${searchTerm}%`,
          ];
          const digits = searchTerm.replace(/\D/g, '');
          if (digits && digits.length > 0 && digits.length <= 6) {
            ors.push(`origin_number.ilike.%${digits.padStart(6, '0')}%`);
          }
          query = query.or(ors.join(','));
        }

        const { data, error } = await query.limit(100);

        if (error) {
          console.error('Erro ao buscar contas a pagar:', error);
        } else if (data) {
          for (const item of data) {
            results.push({
              id: item.id,
              type: 'payable',
              transaction_number: item.transaction_number,
              origin_number: item.origin_number,
              description: item.description,
              due_date: item.due_date,
              payment_date: item.payment_date,
              original_value: item.original_value,
              total_value: item.total_value,
              paid_value: item.paid_value,
              balance: item.balance,
              status: item.status,
              supplier_name: (item.supplier as any)?.name,
              category_name: (item.expense_category as any)?.category_name,
              financial_account_id: item.financial_account_id || undefined,
              financial_account_name: (item.financial_account as any)?.account_name,
            });
          }
        }
      }

      // Ordenar por data de vencimento
      results.sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime());

      return results;
    },
    enabled: !!companyId && enabled,
  });
};
